<template>
    <app-main>
        <app-content>

            <div class="flex flex-wrap -mx-2">
                <div class="w-full md:w-1/2 px-2">
                    <!-- Start Title -->
                    <app-title
                        title="Careers"
                        sub-title="Viewing a career draft"
                    />
                    <!-- End Title -->
                </div>
                <div class="w-full md:w-1/2 px-2 md:text-right">
                     <app-button element="a" class="btn  btn--primary  btn--primary--sml" @click="save">
                        Accept
                    </app-button>

                    <app-button v-if="career" element="a" class="btn  btn--secondary  btn--secondary--sml ml-4" @click="$router.push({ name: 'careers.home' })">
                        Cancel
                    </app-button>
                </div>
            </div>

            <app-tabs-wrapper default="general" :loading="loading" :initial="loading" class="mb-8">
                <div class="w-full flex items-center border-b-2 border-grey-lighter" slot-scope="{ toggle, active }">
                    <app-tabs-trigger name="general" :handle-click="toggle" :active="active">General</app-tabs-trigger>
                    <app-tabs-trigger name="content" :handle-click="toggle" :active="active">Content</app-tabs-trigger>
                    <app-tabs-trigger name="seo" :handle-click="toggle" :active="active">SEO &amp; Search</app-tabs-trigger>
                </div>

                <div slot="items" slot-scope="{ active }">
                    <app-tabs-content name="general" :active="active">
                        <app-field-select 
                            :required="true"
                            label="Locale" 
                            :items="items.locales" 
                            v-model="fields.locale_id" 
                            preselect="&mdash; Please select &mdash;" 
                            :error="errors.locale_id"
                        />

                        <div class="flex flex-wrap -mx-2">
                            <div class="w-full md:w-1/2 px-2">
                                <app-field-text 
                                    :required="true"
                                    label="Title" 
                                    v-model="fields.title" 
                                    :error="errors.title"
                                />
                            </div>
                            <div class="w-full md:w-1/2 px-2">
                                <app-field-text 
                                    :required="true"
                                    label="Slug"
                                    v-model="fields.slug" 
                                    :error="errors.slug"
                                />
                            </div>
                        </div>

                        <app-field-toggle 
                            :required="false"
                            label="Published?" 
                            v-model="fields.published"
                            :error="errors.published"
                        />

                        <app-field-datetime
                            :required="false"
                            label="Published At" 
                            v-if="fields.published" 
                            v-model="fields.published_at" 
                            :error="errors.published_at"
                        />
                    </app-tabs-content>
                    <app-tabs-content name="content" :active="active">
                        <app-field-select 
                            :required="false"
                            label="Location" 
                            :items="items.locations" 
                            v-model="fields.location_id" 
                            :error="errors.location_id"
                        />
                        
                        <app-field-trix 
                            :required="false"
                            label="Description" 
                            v-model="fields.description" 
                            :error="errors.description"
                        />

                         <div class="flex flex-wrap -mx-2">
                            <div class="w-full md:w-1/2 px-2">
                                <app-field-file
                                    :required="false"
                                    label="Thumbnail" 
                                    v-model="fields.thumbnail" 
                                    :error="errors.thumbnail"
                                />
                            </div>
                            <div class="w-full md:w-1/2 px-2">
                                <app-field-file
                                    :required="false"
                                    label="Image" 
                                    v-model="fields.image" 
                                    :error="errors.image"
                                />
                            </div>
                        </div>
                    </app-tabs-content>
                    <app-tabs-content name="seo" :active="active">
                        <div class="flex flex-wrap -mx-2">
                            <div class="w-full md:w-1/2 px-2">
                                <app-field-repeatable
                                    :required="false"
                                    label="Keywords (for searching)" 
                                    placeholder="Enter a keyword"
                                    v-model="fields.keywords" 
                                    :error="errors.keywords"
                                />
                            </div>
                            <div class="w-full md:w-1/2 px-2">
                                <app-field-repeatable
                                    :required="false"
                                    label="Phrases (for searching)" 
                                    placeholder="Enter a phrase"
                                    v-model="fields.phrases" 
                                    :error="errors.phrases"
                                />
                            </div>
                        </div>

                        <app-field-text 
                            :required="false"
                            label="Meta Title" 
                            v-model="fields.meta_title" 
                            :error="errors.meta_title"
                        />

                        <app-field-textarea 
                            :required="false"
                            label="Meta Description" 
                            v-model="fields.meta_desc"
                            :error="errors.meta_desc"
                            rows="4" 
                        />

                        <app-seo-widget :title="fields.meta_title" :description="fields.meta_desc" :slug="fields.slug" />
                    </app-tabs-content>
                </div>
            </app-tabs-wrapper>
        </app-content>
    </app-main>
</template>

<script>
    import { mapGetters } from 'vuex';
    /* API */
    import Api from '@/api/index.js';
    import Auth from '@/api/auth.js';
    /* Components */
    import Main from '@/components/Main';
    import Content from '@/components/template/Content';
    import Title from '@/components/Title';
    import Box from '@/components/Box';
    import Button from '@/components/Button';
    import SelectField from '@/components/fields/inputs/Select';
    import TextField from '@/components/fields/inputs/Text';
    import CategoryField from '@/components/fields/inputs/Category';
    import ToggleField from '@/components/fields/inputs/Toggle';
    import TextareaField from '@/components/fields/inputs/Textarea';
    import DateTimeField from '@/components/fields/inputs/DateTime';
    import TrixField from '@/components/fields/inputs/Trix';
    import CurrencyField from '@/components/fields/inputs/Currency';
    import RepeatableField from '@/components/fields/inputs/Repeatable';
    import FileField from '@/components/fields/inputs/File';
    import TabsWrapper from '@/components/tabs/Wrapper';
    import TabsTrigger from '@/components/tabs/Trigger';
    import TabsContent from '@/components/tabs/Content';
    import SEO from '@/components/SEO';

    /* Mixins */
    import ValidationErrors from '@/mixins/validationErrors.js';

    export default {
        name: 'careers.edit',

        data: () => ({
            career: null,
            fields: {
                locale_id: '',
                location_id: '',
                title: '',
                slug: '',
                description: '',
                thumbnail: '',
                image: '',
                keywords: [],
                phrases: [],
                meta_title: '',
                meta_desc: '',
                published: false,
                published_at: ''
            },
            items: {
                locales: [],
                locations: []
            },
            loading: false,
            errors: {}
        }),

        components: {
            'app-main': Main,
            'app-content': Content,
            'app-title': Title,
            'app-box': Box,
            'app-button': Button,
            'app-field-select': SelectField,
            'app-field-text': TextField,
            'app-field-category': CategoryField,
            'app-field-toggle': ToggleField,
            'app-field-textarea': TextareaField,
            'app-field-datetime': DateTimeField,
            'app-field-trix': TrixField,
            'app-field-currency': CurrencyField,
            'app-field-repeatable': RepeatableField,
            'app-field-file': FileField,
            'app-tabs-wrapper': TabsWrapper,
            'app-tabs-trigger': TabsTrigger,
            'app-tabs-content': TabsContent,
            'app-seo-widget': SEO
        },

        mixins: [
            ValidationErrors
        ],

        mounted() {
            if (typeof this.$route.params.id != 'undefined') {
                this.loading = true;

                this.getLocations()
                    .then(response => {
                        this.getCareer(this.$route.params.id)
                            .then(response => {
                                this.setLocales();

                                for (let index in this.career) {
                                    if (typeof this.fields[index] !== 'undefined') {
                                        switch (index) {
                                            case "published_at":
                                                if(this.career[index]) {
                                                    this.fields[index] = this.career[index].split(' ').join('T') + '.000Z';
                                                }
                                                break;

                                            default:
                                                this.fields[index] = this.career[index];
                                        }
                                    }
                                }
                            })
                            .catch(error => {
                                this.$router.push({name: 'careers.home'});
                            });
                    })
                    .then(response => {
                        this.loading = false;
                    });
            } else {
                this.$router.push({name: 'careers.home'});
            }
        },

        computed: {
            ...mapGetters({
                locales: 'locales/items'
            })
        },

        methods: {
            getLocations() {
                return Api.request('get', `careers/locations`)
                    .then(({ data }) => {
                        this.items.locations = data.locations;
                    });
            },
            getCareer(id) {
                return Api.request('get', `careers/edit/${id}`)
                    .then(({ data }) => {
                        this.career = data.career;
                    });
            },
            save() {
                this.loading = true;
                this.clearErrors();

                return Api.request('post', `careers/update/${this.career.id}`, this.fields).then(response => {
                    if(typeof response.data.message != 'undefined') {
                        this.$toasted.show(response.data.message, { 
                            type: "success", 
                            position: "bottom-right", 
                            duration : 5000
                        });
                    }
                    if(typeof response.data.id != 'undefined') {
                        this.$router.push({
                            name: 'careers.home'
                        });
                    }
                }).catch(error => {
                    if(this.hasErrors(error)) {
                        this.addErrors(error);
                    }
                }).then(response => {
                    this.loading = false;
                });
            },
            setLocales() {
                if (this.locales.length > 0) {
                    this.locales.forEach(locale => {
                        this.items.locales.push({
                            label: locale.lang,
                            value: locale.id
                        });
                    });
                }
            }
        },

        watch: {
            'fields.title': {
                handler(val) {
                    this.fields.slug = val.toString().toLowerCase()
                        .replace(/\s+/g, '-')           // Replace spaces with -
                        .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
                        .replace(/\-\-+/g, '-')         // Replace multiple - with single -
                        .replace(/^-+/, '')             // Trim - from start of text
                        .replace(/-+$/, '');            // Trim - from end of text
                }
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/views/careers/View.vue